import React, { Fragment, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Axios from "axios";
import '../style/GlossaryManagement.scss';
import '../style/CommonButtons.scss';
import Title from "../components/ui/Title";
import Button from "../components/ui/Button";
import FormInputSelect from "../components/ui/FormInputSelect";
import FormInputTextField from "../components/ui/FormInputTextField";
import InputTextField from "../components/ui/InputTextField";
import SwitchButton from "../components/ui/SwitchButton";
import FormInputFile from "../components/ui/FormInputFile";

const JSON_LOCALS = {
    "Afrikaans": { "DeepL": "", "PIM": "af_ZA" },
    "Arabic": { "DeepL": "AR", "PIM": "ar_MA" },
    "German_(AT)": { "DeepL": "DE", "PIM": "de_AT" },
    "German_(CH)": { "DeepL": "DE", "PIM": "de_CH" },
    "German_(DE)": { "DeepL": "DE", "PIM": "de_DE" },
    "English_(GB)": { "DeepL": "EN", "PIM": "en_GB" },
    "English_(AU)": { "DeepL": "EN", "PIM": "en_AU" },
    "English_(CA)": { "DeepL": "EN", "PIM": "en_CA" },
    "English_(CN)": { "DeepL": "EN", "PIM": "en_CN" },
    "English_(IE)": { "DeepL": "EN", "PIM": "en_IE" },
    "English_(SG)": { "DeepL": "EN", "PIM": "en_SG" },
    "English_(US)": { "DeepL": "EN", "PIM": "en_US" },
    "Spanish_(AR)": { "DeepL": "ES", "PIM": "es_AR" },
    "Spanish_(CL)": { "DeepL": "ES", "PIM": "es_CL" },
    "Spanish_(DO)": { "DeepL": "ES", "PIM": "es_DO" },
    "Spanish_(ES)": { "DeepL": "ES", "PIM": "es_ES" },
    "Spanish_(MX)": { "DeepL": "ES", "PIM": "es_MX" },
    "Spanish_(PA)": { "DeepL": "ES", "PIM": "es_PA" },
    "Spanish_(UY)": { "DeepL": "ES", "PIM": "es_UY" },
    "French_(FR)": { "DeepL": "FR", "PIM": "fr_FR" },
    "French_(CA)": { "DeepL": "FR", "PIM": "fr_CA" },
    "Estonian": { "DeepL": "ET", "PIM": "et_EE" },
    "Hebrew": { "DeepL": "HE", "PIM": "he_IL" },
    "Indonesian": { "DeepL": "ID", "PIM": "id_ID" },
    "Italian": { "DeepL": "IT", "PIM": "it_IT" },
    "Japanese": { "DeepL": "JA", "PIM": "ja_JP" },
    "Korean": { "DeepL": "KO", "PIM": "ko_KR" },
    "Latvian": { "DeepL": "LV", "PIM": "lv_LV" },
    "Dutch_(NL)": { "DeepL": "NL", "PIM": "nl_NL" },
    "Dutch_(BE)": { "DeepL": "NL", "PIM": "nl_BE" },
    "Polish": { "DeepL": "PL", "PIM": "pl_PL" },
    "Portuguese_(PT)": { "DeepL": "PT-PT", "PIM": "pt_PT" },
    "Portuguese_(BR)": { "DeepL": "PT-BR", "PIM": "pt_BR" },
    "Romanian": { "DeepL": "RO", "PIM": "ro_RO" },
    "Russian": { "DeepL": "RU", "PIM": "ru_RU" },
    "Swedish": { "DeepL": "SV", "PIM": "sv_SE" },
    "Thai": { "DeepL": "", "PIM": "th_TH" },
    "Turkish": { "DeepL": "TR", "PIM": "tr_TR" },
    "Ukrainian": { "DeepL": "UK", "PIM": "uk_UA" },
    "Chinese_(CN)": { "DeepL": "ZH", "PIM": "zh_CN" },
    "Chinese_(HK)": { "DeepL": "ZH", "PIM": "zh_HK" },
    "Chinese_(TW)": { "DeepL": "ZH", "PIM": "zh_TW" }
};

function findPimLocaleByDeeplCode(deeplCode) {
    return (
      Object.values(JSON_LOCALS).find(l => 
        l.DeepL.toLowerCase() === deeplCode.toLowerCase()
      )?.PIM
    );
}

// ---------- MODALS ----------

function CreateGlossaryModal({ isOpen, onClose, batchId, onGlossaryCreated, userLocales }) {
  const [glossaryName, setGlossaryName] = useState("");
  const [sourceLocale, setSourceLocale] = useState("");
  const [targetLocale, setTargetLocale] = useState("");
  const [tsvContent, setTsvContent] = useState("");
  const [loading, setLoading] = useState(false);

  const apiBaseUrl =
    process.env.NODE_ENV === "development" ? "http://localhost:4000" : "";

  const allLocales =
    userLocales && userLocales.length > 0
      ? userLocales
      : Object.values(JSON_LOCALS).map((l) => l.PIM);

  const selectOptions = allLocales.map(l => ({
    value: l,
    label: l
  }));

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setTsvContent(event.target.result);
    reader.readAsText(file);
  }

  async function handleCreateGlossary() {
    if (!glossaryName || !sourceLocale || !targetLocale || !tsvContent) {
      alert("All fields are required.");
      return;
    }
    setLoading(true);
    try {
      const srcDeepL = Object.values(JSON_LOCALS).find(l => l.PIM === sourceLocale)?.DeepL || sourceLocale;
      const tgtDeepL = Object.values(JSON_LOCALS).find(l => l.PIM === targetLocale)?.DeepL || targetLocale;

      await Axios.post(`${apiBaseUrl}/api/glossary/deepl/create`, {
        batchId: batchId,
        name: glossaryName,
        dictionaries: [
          {
            source_lang: srcDeepL,
            target_lang: tgtDeepL,
            entries: tsvContent,
            entries_format: "tsv"
          }
        ]
      });
      alert("Glossary created!");
      onGlossaryCreated();
    } catch (e) {
      alert(e.response?.data?.error || "Échec de la création du glossaire.");
    }
    setLoading(false);
  }

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <Title textTitle="Créer un nouveau glossaire DeepL" hn="h2" className="modal-title" />
        <div className="modal-field">
          <FormInputTextField
              label="Nom du glossaire"
              type="text"
              value={glossaryName}
              name="glossaryName"
              placeholder="Renseigner le nom"
              onChange={e => setGlossaryName(e.target.value)}              
          />
        </div>
        <div className="modal-field-row">
          <div className="modal-field-50">
            <FormInputSelect
              label="Source"
              options={selectOptions}
              value={sourceLocale}
              onChange={e => setSourceLocale(e.target.value)}
              name="sourceLocale"
              placeholder="Selectionner"
              colorBackgroundInput="transparent"
              colorSelect="black"
              colorOption="black"
            />
          </div>
          <div className="modal-field-50">
            <FormInputSelect
              label="Cible"
              options={selectOptions}
              value={targetLocale}
              onChange={e => setTargetLocale(e.target.value)}
              name="targetLocale"
              placeholder="Selectionner"
              colorBackgroundInput="transparent"
              colorSelect="black"
              colorOption="black"
            />
          </div>
        </div>
        <div className="modal-field">
            <FormInputTextField
              label="Glossaire TSV"
              accept=".tsv,text/tab-separated-values"
              type="file"
              placeholder=""
              onChange={handleFileChange}
              disabled={loading}
              className="file"
            />
          {tsvContent && (
            <div className="modal-file-info">
              Fichier chargé ({tsvContent.split("\n").length} lignes)
            </div>
          )}
        </div>
        <div className="modal-actions">
          <Button
            className="btn btn--hyperlink"
            onClick={onClose}
            disabled={loading}
            textButton="Annuler"
          ></Button>
          <Button
            className="btn btn--primary"
            onClick={handleCreateGlossary}
            disabled={loading}
            textButton="Créer"
          ></Button>
        </div>
      </div>
    </div>
  );
}

function AddTranslationModal({ isOpen, glossary, batchId, onClose, onGlossaryUpdated }) {
  const [existingEntries, setExistingEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const apiBaseUrl =
    process.env.NODE_ENV === "development" ? "http://localhost:4000" : "";

  useEffect(() => {
    if (!isOpen || !glossary) return;
    setLoading(true);
    setExistingEntries([]);
    Axios.get(`${apiBaseUrl}/api/glossary/deepl/${glossary.glossary_id}/dictionaries?batchId=${batchId}`)
      .then(res => {
        const lines = (res.data.entries || "")
          .split("\n").filter(Boolean)
          .filter(line => {
            const [origin, target] = line.split("\t");
            return !!target && origin !== "Source" && origin !== "Français";
          });
        setExistingEntries(lines.map(line => {
          const [origin, target] = line.split("\t");
          return { origin, target };
        }));
      })
      .catch(() => setExistingEntries([]))
      .finally(() => setLoading(false));
  }, [isOpen, glossary, batchId, apiBaseUrl]);

  function handleDownloadTSV() {
    const tsv = existingEntries.map(pair => `${pair.origin}\t${pair.target}`).join('\n');
    const blob = new Blob([tsv], { type: "text/tab-separated-values" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `glossary_${glossary.name || "export"}.tsv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  async function handleFileUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const reader = new FileReader();
    reader.onload = async (event) => {
      const content = event.target.result;
      const lines = content.split("\n").filter(Boolean)
        .map(line => {
          const [origin, target] = line.split('\t');
          return { origin, target };
        })
        .filter(pair => pair.origin && pair.target && pair.origin !== "Source");
      setExistingEntries(lines);

      const tsv = lines.map(pair => `${pair.origin}\t${pair.target}`).join('\n');
      try {
        await Axios.put(`${apiBaseUrl}/api/glossary/deepl/${glossary.glossary_id}/dictionaries`, {
          batchId,
          glossaryId: glossary.glossary_id,
          source_lang: glossary.source_lang,
          target_lang: glossary.target_lang,
          entries: tsv,
          entries_format: "tsv"
        });
        alert("Glossaire importé !");
        onGlossaryUpdated?.();
        setLoading(true);
        Axios.get(`${apiBaseUrl}/api/glossary/deepl/${glossary.glossary_id}/dictionaries?batchId=${batchId}`)
          .then(res => {
            const lines = (res.data.entries || "")
              .split("\n").filter(Boolean)
              .filter(line => {
                const [origin, target] = line.split("\t");
                return !!target && origin !== "Source" && origin !== "Français";
              });
            setExistingEntries(lines.map(line => {
              const [origin, target] = line.split("\t");
              return { origin, target };
            }));
          })
          .catch(() => setExistingEntries([]))
          .finally(() => setLoading(false));
      } catch {
        alert("Erreur lors de l'import du fichier.");
      } finally {
        setUploading(false);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  if (!isOpen || !glossary) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <Title textTitle={`Gérer les traductions pour “${glossary.name}”`} hn="h1" className="glossary-mgmt-title" />
        {/* <h3 className="modal-title">
          Gérer les traductions pour “{glossary.name}”
        </h3> */}
        <div className="modal-field-row ">
          <div className="modal-field-50">
            <InputTextField 
              label="Langue source : "
              type="input"
              disabled={true}
              className=""
              value={glossary.source_lang}
              name="source"
            />
            {/* <label>Langue source : <b>{glossary.source_lang}</b></label> */}
          </div>
          <div className="modal-field-50">
            <InputTextField 
              label="Langue cible : "
              type="input"
              disabled={true}
              className=""
              value={glossary.target_lang}
              name="cible"
            />
            {/* <label>Langue cible : <b>{glossary.target_lang}</b></label> */}
          </div>
        </div>
        <div className="modal-warning">
          ⚠️ <b>Attention :</b> Chaque modification ou ajout écrase tout le glossaire existant.<br />
          Pensez à <b>télécharger</b> une sauvegarde avant de modifier.
        </div>
        <div className="modal-field">
          <Title textTitle="Glossaire TSV actuel" hn="h4" className="" />
          <div className="modal-actions-row-container">
            <Button
              className="btn btn--primary"
              onClick={handleDownloadTSV}
              disabled={loading || uploading}
              textButton="Télécharger le glossaire (TSV)"
            ></Button>
            <span className="modal-or-text">Ou</span>
            <FormInputFile
              name="entryFile"
              label="Importer un glossaire (TSV)"
              accept=".tsv,text/tab-separated-values"
              onChange={handleFileUpload}
              disabled={loading || uploading}              
            />
            {/* <label className="greenButton file-input-label">
            📤 Importer un glossaire (TSV)
            <input
              type="file"
              accept=".tsv,text/tab-separated-values"
              style={{ display: "none" }}
              onChange={handleFileUpload}
              disabled={loading || uploading}
            />
          </label> */}
          </div>
        </div>
        {uploading && (
          <div className="modal-uploading">Import en cours...</div>
        )}
        {/* <div>
            <Title textTitle="Entrées existantes" hn="h5" className="" />
            { loading ? 
            (
                <div>Chargement...</div>
            ) : (
                <div className="modal-entries-list">
                    {existingEntries.length === 0 && (
                        <div>Aucune entrée</div>
                    )}
                    {existingEntries.map((pair, idx) => (
                        <Fragment key={idx}>                        
                            <div className="modal-entries-items">
                                {pair.origin}
                            </div>
                            <div className="modal-entries-items">
                                {pair.target} 
                            </div>
                        </Fragment>
                    ))}
                </div>

            // <table className="modal-entries-table">
            //     <tbody>
            //     {existingEntries.length === 0 && (
            //         <tr>
            //         <td colSpan={2} className="modal-empty-entry">Aucune entrée</td>
            //         </tr>
            //     )}
            //     {existingEntries.map((pair, idx) => (
            //         <tr key={idx}>
            //         <td>{pair.origin}</td>
            //         <td>{pair.target}</td>
            //         </tr>
            //     ))}
            //     </tbody>
            // </table>
            )}
        </div> */}
        <div className="modal-actions modal-actions-top">
          <Button
            className="btn btn--hyperlink--dark"
            onClick={onClose}
            textButton="Fermer"
          ></Button>
          {/* <button
            type="button"
            className="blackButton"
            onClick={onClose}
            disabled={loading || uploading}
          >
            Fermer
          </button> */}

        </div>
      </div>
    </div>
  );
}

// -------- MAIN COMPONENT ----------

const GlossaryManagement = () => {
    const apiBaseUrl =
        process.env.NODE_ENV === "development"
            ? "http://localhost:4000"
            : "";

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const batchId = searchParams.get('batchId');
    const navigate = useNavigate();

    const [batch, setBatch] = useState(null);
    const [userLocales, setUserLocales] = useState([]);
    const [originLocale, setOriginLocale] = useState("");
    const [targetLocale, setTargetLocale] = useState("");
    const [deeplGlossaries, setDeeplGlossaries] = useState([]);
    const [activeMappings, setActiveMappings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedGlossary, setSelectedGlossary] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [createModalOpen, setCreateModalOpen] = useState(false);

    function openModal(gloss) {
      setSelectedGlossary(gloss);
      setModalOpen(true);
    }
    function closeModal() {
      setModalOpen(false);
      setSelectedGlossary(null);
      setRefreshKey(k => k + 1)
    }

    async function handleSubmitEntries(entries) {
      if (!selectedGlossary) return;
      setSaving(true);
      try {
        await Axios.put(
          `${apiBaseUrl}/api/glossary/deepl/${selectedGlossary.glossary_id}/dictionaries`,
          {
            batchId,
            source_lang: selectedGlossary.source_lang,
            target_lang: selectedGlossary.target_lang,
            entries,
            entries_format: "tsv"
          }
        );
        alert("Glossaire mis à jour");
        closeModal();
      } catch (e) {
        alert(e.response?.data?.error || "Erreur lors de la mise à jour du glossaire");
      }
      setSaving(false);
    }

    // 1️⃣ Fetch batch
    useEffect(() => {
        if (!batchId) {
            setFetchError("batchId manquant dasn l'URL");
            setLoading(false);
            return;
        }
        setLoading(true);
        Axios.get(`${apiBaseUrl}/batch/${batchId}`)
            .then(res => {
                setBatch(res.data);
                setFetchError(null);
            })
            .catch(e => {
                setFetchError(e.response?.data?.error || "Batch introuvable");
            })
            .finally(() => setLoading(false));
    }, [batchId]);

    // 2️⃣ Fetch user locales from Quable
    useEffect(() => {
        if (!batch) return;
        setLoading(true);
        Axios.get(`${batch.quableInstance}/users/${batch.quableUserID}`, {
            headers: { Authorization: "Bearer " + batch.token }
        })
            .then(res => {
                setUserLocales(res.data.localesAvailable || []);
                setOriginLocale(res.data.localesAvailable?.[0] || "");
                setTargetLocale(res.data.localesAvailable?.[1] || "");
                setFetchError(null);
            })
            .catch(e => {
                setFetchError(e.response?.data?.error || "Échec du chargement des paramètres utilisateur depuis Quable");
            })
            .finally(() => setLoading(false));
    }, [batch]);

    // 3️⃣ Fetch all DeepL glossaries for this batch
    useEffect(() => {
        if (!batchId) return;
        Axios.get(`${apiBaseUrl}/api/glossary/deepl/glossaries?batchId=${batchId}`)
            .then((res) => setDeeplGlossaries(res.data || []))
            .catch(() => setDeeplGlossaries([]));
    }, [batchId, saving, refreshKey]);

    // 4️⃣ Fetch current active mappings
    useEffect(() => {
        if (!batchId) return;
        Axios.get(`${apiBaseUrl}/api/glossary?batchId=${batchId}`)
            .then((res) => setActiveMappings(res.data || []))
            .catch(() => setActiveMappings([]));
    }, [batchId, saving]);

    // 5️⃣ Helper: Is this glossary enabled?
    const isGlossaryActive = (deeplGlossary) =>
        !!activeMappings.find(g => g.glossaryId === deeplGlossary.glossary_id);

    async function handleEnableGlossary(deeplGlossary) {
        setSaving(true);

        const pimSourceLocale = findPimLocaleByDeeplCode(deeplGlossary.source_lang) || deeplGlossary.source_lang;
        const pimTargetLocale = findPimLocaleByDeeplCode(deeplGlossary.target_lang) || deeplGlossary.target_lang;

        try {
            await Axios.post(`${apiBaseUrl}/api/glossary`, {
                batchId,
                originLocale: pimSourceLocale,
                targetLocale: pimTargetLocale,
                glossaryId: deeplGlossary.glossary_id,
                name: deeplGlossary.name,
                source_lang: deeplGlossary.source_lang,
                target_lang: deeplGlossary.target_lang,
                ready: deeplGlossary.ready,
                entry_count: deeplGlossary.entry_count,
                creation_time: deeplGlossary.creation_time
            });
        } catch (e) {
            alert(e.response?.data?.error || "Échec de la désactivation du glossaire");
        }
        setSaving(false);
    }

    async function handleDisableGlossary(deeplGlossary) {
        setSaving(true);
        try {
            const mapping = activeMappings.find(g => g.glossaryId === deeplGlossary.glossary_id);
            if (!mapping) return;
            await Axios.delete(`${apiBaseUrl}/api/glossary/${mapping._id}`, { data: { batchId } });
        } catch (e) {
            alert(e.response?.data?.error || "Échec de la désactivation du glossaire");
        }
        setSaving(false);
    }

    if (loading) return <div>Loading...</div>;
    if (fetchError) return <div style={{ color: "red" }}>{fetchError}</div>;

    return (
        <div className="glossary-mgmt-root">
            <div className="glossary-mgmt-wrapper">
                <div className="glossary-mgmt-root-container">
                  <div className="glossary-mgmt-header">
                    <div className="glossary-mgmt-header-left">
                      <Title textTitle="Gestion du Glossaire DeepL" hn="h1" className="glossary-mgmt-title" />
                      <Button
                          className="btn btn--hyperlink"
                          onClick={() => setCreateModalOpen(true)}
                          textButton="+ Créer un glossaire DeepL"
                      ></Button>
                    </div>
                    <div className="glossary-mgmt-header-right">
                      <Button
                          className="btn btn--hyperlink--dark"
                          onClick={() => navigate(-1)}
                          textButton="Retour →"
                      ></Button>
                      {/* <button style={{ marginTop: 32 }} className="blueButton" onClick={() => navigate(-1)}>
                          ← Back
                      </button> */}
                    </div>
                  </div>
                    <CreateGlossaryModal
                    isOpen={createModalOpen}
                    onClose={() => setCreateModalOpen(false)}
                    batchId={batchId}
                    onGlossaryCreated={() => {
                        setCreateModalOpen(false);
                        setRefreshKey((k) => k + 1);
                    }}
                    userLocales={userLocales}
                    />
                    <div style={{ marginTop: 32, overflowX: 'auto' }}>
                        <table className="glossary-mgmt-table">
                            <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Source</th>
                                    <th>Cible</th>
                                    {/* <th>Prêt</th> */}
                                    <th>Nbre de mots</th>
                                    <th>Créés le</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deeplGlossaries.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="glossary-mgmt-empty">
                                            Aucun glossaire DeepL trouvé pour ce compte.
                                        </td>
                                    </tr>
                                )}
                                {deeplGlossaries.map((gloss) => (
                                    <tr key={gloss.glossary_id}>
                                        <td>{gloss.name}</td>
                                        <td>{gloss.source_lang}</td>
                                        <td>{gloss.target_lang}</td>
                                        {/* <td>{gloss.ready ? "Yes" : "No"}</td> */}
                                        <td>{gloss.entry_count}</td>
                                        <td>{gloss.creation_time && gloss.creation_time.slice(0, 10)}</td>
                                        <td className="glossary-mgmt-actions-td">
                                            <SwitchButton
                                                isActive={isGlossaryActive(gloss)}
                                                onEnable={() => handleEnableGlossary(gloss)}
                                                onDisable={() => handleDisableGlossary(gloss)}
                                                disabled={saving || (!isGlossaryActive(gloss) && !gloss.ready)}
                                                activeText="Activé"
                                                inactiveText="Désactivé"
                                            />
                                            {/* {isGlossaryActive(gloss) ? (
                                                <button
                                                    className="redButton"
                                                    disabled={saving}
                                                    onClick={() => handleDisableGlossary(gloss)}
                                                >
                                                    Disable
                                                </button>
                                            ) : (
                                                <button
                                                    className="blueButton"
                                                    disabled={saving || !gloss.ready}
                                                    onClick={() => handleEnableGlossary(gloss)}
                                                >
                                                    Enable
                                                </button>
                                            )} */}
                                            <Button
                                                className="btn btn--primary"
                                                onClick={() => openModal(gloss)}
                                                disabled={saving}
                                                textButton="Ajouter des traductions"
                                            ></Button>
                                            {/* <button
                                            className="orangeButton"
                                            onClick={() => openModal(gloss)}
                                            disabled={saving}
                                            >
                                            Add Translations
                                            </button> */}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                
            </div>
            

            
            <AddTranslationModal
              isOpen={modalOpen}
              glossary={selectedGlossary}
              batchId={batchId}
              onClose={closeModal}
            />
        </div>
    );
};

export default GlossaryManagement;
